import {NgModule} from '@angular/core';
import {RouterModule, Routes} from '@angular/router';
import {AdminLayoutComponent} from './layout/admin-layout/admin-layout.component';
import {ItinerariesComponent} from './components/itineraries/itineraries.component';
import {ItineraryTemplatesComponent} from './components/itinerary-templates/itinerary-templates.component';
import {AddItineraryTemplateComponent} from './components/itinerary-templates/add-itinerary-template/add-itinerary-template.component';
import {UsersComponent} from './components/users/users.component';
import {AddEditUserComponent} from './components/users/add-edit-user/add-edit-user.component';
import {RolesComponent} from './components/roles/roles.component';
import {PreviewItineraryComponent} from './components/external/preview-itinerary/preview-itinerary.component';
import {AddRoleComponent} from './components/roles/add-role/add-role.component';
import {AuthGuard} from './core/guards/auth.guard';
import {CustomersComponent} from './components/customers/customers.component';
import {AddEditCustomerComponent} from './components/customers/add-edit-customer/add-edit-customer.component';
import {SettingsComponent} from './components/settings/settings.component';
import {DestinationsComponent} from './components/contents/destinations/destinations.component';
import {McDestinationComponent} from './components/contents/destinations/mc-destination/mc-destination.component';
import {OtherContentComponent} from './components/contents/other-content/other-content.component';
import {AddEditItineryComponent} from './components/itineraries/add-edit-itinery/add-edit-itinery.component';
import {DashboardComponent} from './components/dashboard/dashboard.component';
import {ExperiencesComponent} from "./components/contents/experiences/experiences.component";
import {McExperiencesComponent} from "./components/contents/experiences/mc-experiences/mc-experiences.component";
import {CostBreakdownComponent} from "./components/itineraries/cost-breakdown/cost-breakdown.component";
import { UnauthorizedComponent } from './components/unauthorized/unauthorized.component';
import {
  PreviewItinararyTwoComponent
} from './components/external/preview-itinarary-two/preview-itinarary-two.component';
import { ViewStatusLogComponent } from './components/itineraries/view-status-log/view-status-log.component';
import { UnsavedChangesGuard } from './core/guards/unsaved-changes.guard';
import { ViewVehicleImageComponent } from './components/external/view-vehicle-image/view-vehicle-image.component';

const routes: Routes = [
  // Static Pages
  {
    path: '',
    redirectTo: '/admin/dashboard',
    pathMatch: 'full',
  },
  // Handling User authentication
  {
    path: 'auth',
    loadChildren: () =>
      import('./modules/auth/auth.module').then(m => m.AuthModule),
  },
  // App Components
  {
    path: 'admin',
    component: AdminLayoutComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: '',
        redirectTo: '/admin/dashboard',
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        component: DashboardComponent,
      },
      {
        path: 'itineraries',
        children: [
          {
            path: '',
            component: ItinerariesComponent,
          },
          {
            path: 'add',
            component: AddEditItineryComponent,
          },
          {
            path: 'template',
            component: ItineraryTemplatesComponent,
          },
          {
            path: 'template/add',
            component: AddEditItineryComponent,
          },
          {
            path: 'edit/details/:iti',
            component: AddEditItineryComponent,
          },
          {
            path: 'edit/:id',
            component: AddEditItineryComponent,
            canDeactivate: [UnsavedChangesGuard],
          },
          {
            path: "costbreakdown/:id",
            component: CostBreakdownComponent,
          },
          {
            path: "status-logs/:id/:tour",
            component: ViewStatusLogComponent,
          },
        ],
      },
      {
        path: 'template-itinerary',
        children: [
          {
            path: '',
            component: ItineraryTemplatesComponent,
          },
          {
            path: 'add',
            component: AddItineraryTemplateComponent,
          },
          {
            path: 'edit/details/:iti',
            component: AddItineraryTemplateComponent,
          },
          {
            path: 'edit/:id',
            component: AddEditItineryComponent,
            canDeactivate: [UnsavedChangesGuard],
          },
        ],
      },
      {
        path: 'users',
        children: [
          {
            path: '',
            component: UsersComponent,
          },
          {
            path: 'add',
            component: AddEditUserComponent,
          },
          {
            path: 'edit/:id',
            component: AddEditUserComponent,
          },
        ],
      },
      {
        path: 'roles',
        children: [
          {
            path: '',
            component: RolesComponent,
          },
          {
            path: 'add',
            component: AddRoleComponent,
          },
          {
            path: 'edit/:id',
            component: AddRoleComponent,
          },
        ],
      },
      {
        path: 'payment',
        loadChildren: () =>
          import('./modules/chargebee/chargebee.module').then(m => m.ChargebeeModule),
      },
      {
        path: "agents",
        loadChildren: () =>
          import('./modules/agent/agent.module').then(
            m => m.AgentModule
          ),
      },
      {
        path: "rates",
        loadChildren: () =>
          import('./modules/rates/rates.module').then(
            m => m.RatesModule
          ),
      },
      {
        path: 'customers',
        children: [
          {
            path: '',
            component: CustomersComponent,
          },
          {
            path: 'add',
            component: AddEditCustomerComponent,
          },
          {
            path: 'edit/:id',
            component: AddEditCustomerComponent,
          },
        ],
      },
      {
        path: 'customers',
        component: CustomersComponent,
      },
      {
        path: 'customers/add',
        component: AddEditCustomerComponent,
      },
      {
        path: 'customers/edit/:id',
        component: AddEditCustomerComponent,
      },
      {
        path: 'content',
        children: [
          {
            path: 'destinations',
            children: [
              {
                path: '',
                component: DestinationsComponent,
              },
              {
                path: 'add',
                component: McDestinationComponent,
              },
              {
                path: 'edit/:id',
                component: McDestinationComponent,
              },
            ],
          },
          {
            path: 'experiences',
            children: [
              {
                path: '',
                component: ExperiencesComponent,
              },
              {
                path: 'add',
                component: McExperiencesComponent,
              },
              {
                path: 'edit/:id',
                component: McExperiencesComponent,
              },
            ],
          },
          {
            path: 'other',
            component: OtherContentComponent,
          },
        ],
      },
      {
        path: 'settings',
        children: [
          {
            path: '',
            component: SettingsComponent,
          },
          {
            path: 'account/:index',
            component: SettingsComponent,
          },
        ],
      },
    ],
  },
  {
    path: 'client',
    children: [
      {
        path: 'preview/t1/:id/:watched',
        component: PreviewItineraryComponent,
      },
      {
        path: 'preview/t2/:id/:watched',
        component: PreviewItinararyTwoComponent,
      },
      {
        path: 'view-vehicle/:url/:name',
        component: ViewVehicleImageComponent,
      },
    ],
  },
  {
    path: "btob",
    children: [
      {
        path: "preview/t1/:id/:watched",
        component: PreviewItineraryComponent,
      },
      {
        path: "preview/t2/:id/:watched",
        component: PreviewItinararyTwoComponent,
      },
    ],
  },
  {
    path: "unauthorized",
    component: UnauthorizedComponent,
  }
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: "top"
    })
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {
}
